import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Edit2, Trash2, Trophy, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { HeadlineOverlayVariation } from '@/store/videoFeatures/types/headline-overlay';
import { HeadlinePreviewMini } from './HeadlinePreviewMini';
import { cn } from '@/lib/utils';

interface HeadlineVariationCardProps {
  variation: HeadlineOverlayVariation;
  isActive?: boolean;
  isPrimary?: boolean;
  trafficPercentage: number;
  onEdit: (variation: HeadlineOverlayVariation) => void;
  onDelete: (variationId: string) => void;
  canDelete?: boolean;
}

export const HeadlineVariationCard: React.FC<HeadlineVariationCardProps> = ({
  variation,
  isActive = false,
  isPrimary = false,
  trafficPercentage,
  onEdit,
  onDelete,
  canDelete = true
}) => {
  useLanguage();
  return (
    <div
      className={cn(
        "bg-card rounded-xl p-4 group",
        "shadow-[0_2px_12px_rgba(0,0,0,0.04)]",
        "hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)]",
        "transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
        "border-2",
        isPrimary
          ? "border-primary shadow-[0_0_0_3px_hsl(var(--primary)_/_0.1)]"
          : "border-border hover:border-primary/40"
      )}
    >
      {/* Header premium - hierarquia melhorada */}
      <div className="flex items-start gap-2 mb-3">
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          {/* Linha 1: Badges + Percentual */}
          <div className="flex items-center gap-1.5">
            {isPrimary && (
              <Badge className="bg-primary/10 text-primary border-primary/30 text-[10px] px-2 py-0.5 h-5 shrink-0 font-medium">
                {translateInterfaceText("Principal")}</Badge>
            )}
            {!isPrimary && isActive && (
              <Badge className="bg-blue-50 text-blue-600 border-blue-200 gap-1 text-[9px] px-1.5 py-0.5 h-4 shrink-0">
                <span className="relative flex h-1 w-1">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1 w-1 bg-blue-500"></span>
                </span>
                {translateInterfaceText("Em teste")}</Badge>
            )}
            
            {/* Performance Badges */}
            {variation.metrics?.ctr > 5 && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-300 gap-1 text-[9px] px-1.5 py-0.5 h-4 shrink-0 font-medium">
                <Trophy className="h-2.5 w-2.5" />
                {translateInterfaceText("Melhor")}</Badge>
            )}
            {variation.metrics?.engagement > 70 && variation.metrics?.ctr <= 5 && (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 gap-1 text-[9px] px-1.5 py-0.5 h-4 shrink-0 font-medium">
                <TrendingUp className="h-2.5 w-2.5" />
                {translateInterfaceText("Alto Engajamento")}</Badge>
            )}
            
            {/* Percentual alinhado à direita */}
            <span className="ml-auto text-sm font-semibold text-foreground tabular-nums shrink-0">
              {trafficPercentage}%
            </span>
          </div>
          
        </div>
      </div>

      {/* Preview com overlay de ações (padrão Autoplay) */}
      <div className="relative aspect-[16/10] h-[120px] bg-slate-900 rounded-lg overflow-hidden group/preview">
        {/* Fundo escuro simulando vídeo */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />
        
        {/* Preview da headline centralizado */}
        <div className="absolute inset-0 flex items-center justify-center p-3">
          <HeadlinePreviewMini 
            headline={variation.headline}
            subheadline={variation.subheadline}
            className="max-w-[85%] transform scale-[0.85]"
          />
        </div>

        {/* Overlay de ações (hover) - mesmo padrão do Autoplay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/preview:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-6">
          <button
            type="button"
            className="flex flex-col items-center gap-1 transition-transform hover:scale-110"
            onClick={() => onEdit(variation)}
          >
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-lg">
              <Edit2 className="h-4 w-4 text-primary" />
            </div>
            <span className="text-[10px] text-white font-medium">{translateInterfaceText("Editar")}</span>
          </button>
          
          <button
            type="button"
            className="flex flex-col items-center gap-1 transition-transform hover:scale-110 disabled:opacity-50 disabled:pointer-events-none"
            onClick={() => onDelete(variation.id)}
            disabled={!canDelete}
          >
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-lg">
              <Trash2 className="h-4 w-4 text-destructive" />
            </div>
            <span className="text-[10px] text-white font-medium">{translateInterfaceText("Excluir")}</span>
          </button>
        </div>
      </div>

      {/* Footer: Nome + Tags (padrão Autoplay) */}
      <div className="mt-2 pt-2 border-t border-border/50 flex items-center justify-between gap-2">
        {/* Nome (lado esquerdo) - com limite de 25 caracteres visíveis */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-xs font-medium text-foreground truncate max-w-[120px] cursor-default">
              {variation.name.length > 25 
                ? `${variation.name.slice(0, 25)}...` 
                : variation.name}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[240px]">
            <p className="font-medium">{variation.name}</p>
            {variation.name.length > 25 && (
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {variation.name.length}/25 caracteres
              </p>
            )}
          </TooltipContent>
        </Tooltip>
        
        {/* Tags (lado direito) - Tamanho + Fonte como chips */}
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium capitalize">
            {variation.headline.size}
          </span>
          {variation.headline.fontFamily !== 'Inter' && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
              {variation.headline.fontFamily}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
